import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { useArtworkTemplates, useArtworkTemplate } from "@/lib/artwork-templates";
import {
  datasheetsApi, useInvalidateDatasheet,
  type OrderBadgeInput, type DerivedOrderQuantities,
} from "@/lib/datasheets";

interface BadgeRow {
  fieldValues: string[]; quantity: string;
  deliverySuburb: string; deliveryPostcode: string;
}
const emptyRow = (fieldCount: number): BadgeRow => ({
  fieldValues: Array.from({ length: fieldCount }, () => ""), quantity: "",
  deliverySuburb: "", deliveryPostcode: "",
});

const toInput = (r: BadgeRow): OrderBadgeInput => ({
  fieldValues: r.fieldValues,
  quantity: Number(r.quantity) || 0,
  fittingId: null,
  deliveryTo: null, deliveryStreet: null,
  deliverySuburb: r.deliverySuburb || null, deliveryPostcode: r.deliveryPostcode || null,
});

export function BuildDatasheetOrderPage() {
  const { id } = useParams();
  const datasheetId = Number(id);
  const navigate = useNavigate();
  const invalidate = useInvalidateDatasheet();

  const { data: templates } = useArtworkTemplates(datasheetId);
  const [templateId, setTemplateId] = useState<number | null>(null);
  useEffect(() => {
    const only = templates?.length === 1 ? templates[0] : undefined;
    if (templateId === null && only) setTemplateId(only.artworkTemplateId);
  }, [templates, templateId]);

  const { data: template } = useArtworkTemplate(templateId ?? 0);
  const fields = useMemo(() => (template?.fields ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder), [template]);
  const fieldCount = fields.length;

  const [rows, setRows] = useState<BadgeRow[]>([]);
  const [po, setPo] = useState("");
  const [derived, setDerived] = useState<DerivedOrderQuantities | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset to one fresh row whenever the chosen template (or its field set) changes.
  useEffect(() => {
    if (templateId !== null && fieldCount > 0) setRows([emptyRow(fieldCount)]);
  }, [templateId, fieldCount]);

  const setRow = (i: number, patch: Partial<BadgeRow>) => setRows((p) => p.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setCell = (i: number, f: number, v: string) =>
    setRows((p) => p.map((r, j) => (j === i ? { ...r, fieldValues: r.fieldValues.map((x, k) => (k === f ? v : x)) } : r)));

  const hasSomething = rows.some((r) => Number(r.quantity) > 0);

  useEffect(() => {
    if (templateId === null || !hasSomething) { setDerived(null); return; }
    const handle = setTimeout(() => {
      datasheetsApi.derivePreview(datasheetId, { artworkTemplateId: templateId, badges: rows.filter((r) => Number(r.quantity) > 0).map(toInput) })
        .then(setDerived).catch(() => setDerived(null));
    }, 300);
    return () => clearTimeout(handle);
  }, [datasheetId, templateId, hasSomething, rows]);

  async function onImport(file: File | undefined) {
    if (!file || templateId === null) return;
    try {
      const parsed = await datasheetsApi.importBadges(datasheetId, templateId, file);
      setRows(parsed.map((b) => ({
        fieldValues: Array.from({ length: fieldCount }, (_, k) => b.fieldValues[k] ?? ""),
        quantity: b.quantity ? String(b.quantity) : "",
        deliverySuburb: b.deliverySuburb ?? "", deliveryPostcode: b.deliveryPostcode ?? "",
      })));
      toast.success(`Imported ${parsed.length} badge${parsed.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Could not import the spreadsheet");
    }
  }

  async function placeOrder() {
    if (templateId === null) return;
    setSubmitting(true);
    try {
      const badges = rows.filter((r) => Number(r.quantity) > 0).map(toInput);
      const built = await datasheetsApi.buildOrder(datasheetId, {
        artworkTemplateId: templateId, badges, requestedByContactId: null, note: null,
      });
      const commit = await datasheetsApi.commitOrder(datasheetId, built.draft.datasheetUsageId, {
        sizeQuantities: built.derived.sizeQuantities,
        deliveryChargeCount: built.derived.deliveryChargeCount,
        prepaidFittingQuantity: built.derived.prepaidFittingQuantity,
        requestedByContactId: null, note: null, dueDate: null, deliveryMethodId: null,
        deliveryAddressId: null, deliveryTo: null, deliveryAttention: null,
        doNotLabelAsAllmark: false, clientPurchaseOrderNo: po || null,
      });
      invalidate(datasheetId);
      for (const w of commit.warnings) toast.info(w.message);
      toast.success(`Order placed — job ${commit.jobReferenceNo}`);
      void navigate(`/jobs/${commit.jobId}`);
    } catch (e) {
      toast.error(e instanceof ApiError && e.status === 400 ? "Invalid — check the badges." : "Could not place the order");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <h1 className="text-xl font-semibold">Build order from badges</h1>

      <div className="space-y-1">
        <Label htmlFor="bo-template">Artwork template</Label>
        <select id="bo-template" aria-label="Artwork template"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={templateId ?? ""} onChange={(e) => setTemplateId(e.target.value === "" ? null : Number(e.target.value))}>
          <option value="">Select template…</option>
          {(templates ?? []).map((t) => <option key={t.artworkTemplateId} value={t.artworkTemplateId}>{t.name}</option>)}
        </select>
      </div>

      {templateId !== null && fieldCount > 0 ? (
        <div className="space-y-2">
          <Label>Badges</Label>
          <div className="flex items-center gap-2">
            <Label htmlFor="bo-import" className="text-sm">Import from Excel</Label>
            <input id="bo-import" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              aria-label="Import from Excel" onChange={(e) => void onImport(e.target.files?.[0])} />
          </div>
          {rows.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 border-b pb-2">
              {fields.map((f, k) => (
                <Input key={f.artworkTemplateFieldId} aria-label={`Badge ${i + 1} ${f.fieldName}`}
                  placeholder={f.fieldName} className="w-40"
                  value={r.fieldValues[k] ?? ""} onChange={(e) => setCell(i, k, e.target.value)} />
              ))}
              <Input aria-label={`Badge ${i + 1} quantity`} type="number" min={0} className="w-24" placeholder="Qty"
                value={r.quantity} onChange={(e) => setRow(i, { quantity: e.target.value })} />
              <Input aria-label={`Badge ${i + 1} suburb`} className="w-32" placeholder="Suburb (delivery)"
                value={r.deliverySuburb} onChange={(e) => setRow(i, { deliverySuburb: e.target.value })} />
              <Input aria-label={`Badge ${i + 1} postcode`} className="w-24" placeholder="Postcode"
                value={r.deliveryPostcode} onChange={(e) => setRow(i, { deliveryPostcode: e.target.value })} />
              {rows.length > 1 ? (
                <Button type="button" variant="ghost" size="sm" aria-label={`Remove badge ${i + 1}`}
                  onClick={() => setRows((p) => p.filter((_, j) => j !== i))}>×</Button>
              ) : null}
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => setRows((p) => [...p, emptyRow(fieldCount)])}>Add badge</Button>
        </div>
      ) : null}

      {derived ? (
        <div className="rounded-md border p-3 text-sm">
          <p className="font-medium">Derived draw-down</p>
          <p>Sizes: {derived.sizeQuantities.map((q, i) => (q ? `#${i + 1}: ${q}` : null)).filter(Boolean).join("  ") || "—"}</p>
          <p>Delivery charges: {derived.deliveryChargeCount} · Fittings: {derived.prepaidFittingQuantity}</p>
          {derived.warnings.map((w, i) => <p key={i} className="text-xs text-amber-600">{w}</p>)}
        </div>
      ) : null}

      <div className="space-y-1">
        <Label htmlFor="bo-po">Customer PO</Label>
        <Input id="bo-po" className="max-w-xs" value={po} onChange={(e) => setPo(e.target.value)} />
      </div>

      <div className="flex gap-2">
        <Button onClick={() => void placeOrder()} disabled={!hasSomething || submitting}>Place order</Button>
        <Button variant="outline" onClick={() => void navigate(`/datasheets/${datasheetId}`)}>Cancel</Button>
      </div>
    </div>
  );
}
